import gql from 'graphql-tag';
import { GraphQLFormattedError } from 'graphql';
import {
  ApolloClient,
  NormalizedCacheObject,
  ApolloError,
  TypedDocumentNode,
} from '@apollo/client';
import { FastifyLoggerInstance } from 'fastify';
import {
  seoSegmentationsByCityOrStateVariables,
  seoSegmentationsByCityOrState as seoSegmentationsByCityOrStateQueryType,
  seoSegmentationsByCityOrState_seoSegmentationsByCityOrState_SEOSegmentationsByCityOrStateSuccess_seoSegmentationsByCityOrState as seoSegmentationsByCityOrStateType,
  seoSegmentationsByCityOrState_seoSegmentationsByCityOrState_SEOSegmentationsByCityOrStateError_error as seoSegmentationsByCityOrStateErrorType,
} from '@/__generated__/seoSegmentationsByCityOrState';
import { apolloClientQuery } from './utils';

const SEO_SEGMENTATIONS_BY_CITY_OR_STATE: TypedDocumentNode<
  seoSegmentationsByCityOrStateQueryType,
  seoSegmentationsByCityOrStateVariables
> = gql`
  query seoSegmentationsByCityOrState($seoSegmentId: ID!) {
    seoSegmentationsByCityOrState(seoSegmentId: $seoSegmentId) {
      __typename
      ... on SEOSegmentationsByCityOrStateSuccess {
        seoSegmentationsByCityOrState {
          city
          linkSection {
            section {
              linkList {
                content
                url
              }
              subSectionId
              subSectionTitle {
                ... on SEOLinkSectionTitle {
                  title
                }
                ... on SEOLinkSectionLinkedTitle {
                  linkedTitle {
                    content
                    url
                  }
                }
              }
            }
            sectionId
          }
          state
        }
      }
      ... on SEOSegmentationsByCityOrStateError {
        error {
          message
          ... on DataNotAvailableError {
            message
          }
          ... on UrlNotFoundError {
            message
          }
        }
      }
    }
  }
`;

type SEOSegmentationsByCityOrStateQuery = (
  client: ApolloClient<NormalizedCacheObject>,
  log: FastifyLoggerInstance,
  variables: seoSegmentationsByCityOrStateVariables
) => Promise<
  | {
      seoSegmentationsByCityOrState: seoSegmentationsByCityOrStateType;
      error: null;
    }
  | {
      seoSegmentationsByCityOrState: null;
      error:
        | ApolloError
        | seoSegmentationsByCityOrStateErrorType
        | readonly GraphQLFormattedError[];
    }
>;

const seoSegmentationsByCityOrState: SEOSegmentationsByCityOrStateQuery = async (
  client,
  log,
  variables
) => {
  const { queryResponse, error } = await apolloClientQuery(
    client,
    SEO_SEGMENTATIONS_BY_CITY_OR_STATE,
    variables
  );

  // unknown error
  if (error) {
    return {
      seoSegmentationsByCityOrState: null,
      error,
    };
  }

  const { data, errors, error: responseError } = queryResponse;

  // GraphQL errors that occur while processing a GraphQL operation (resulting in built-in error codes such as INTERNAL_SERVER_ERROR)
  if (errors && errors.length > 0) {
    return {
      seoSegmentationsByCityOrState: null,
      error: errors,
    };
  }

  // Coverage for 'error' property in queryResponse
  if (responseError) {
    return {
      seoSegmentationsByCityOrState: null,
      error: responseError,
    };
  }

  // known error
  if (data.seoSegmentationsByCityOrState.__typename === 'SEOSegmentationsByCityOrStateError') {
    const { message: errorMessage } = data.seoSegmentationsByCityOrState.error;
    log.error(`[GraphQL error]: ${errorMessage}`);

    return {
      seoSegmentationsByCityOrState: null,
      error: data.seoSegmentationsByCityOrState.error,
    };
  }

  const segmentationsList = data.seoSegmentationsByCityOrState.seoSegmentationsByCityOrState;

  return { seoSegmentationsByCityOrState: segmentationsList, error: null };
};

export { SEO_SEGMENTATIONS_BY_CITY_OR_STATE, seoSegmentationsByCityOrState };
